import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSiteContent } from "@/lib/siteContent";
import { resolveImageUrl } from "@/lib/api";
import { HotelSwitcher } from "@/components/HotelSwitcher";
import { SupportWidget } from "@/components/support/SupportWidget";
import {
    LayoutDashboard, ClipboardList, CalendarDays, MessageSquare, Settings,
    LogOut, Menu, X, ExternalLink, Sun, Moon, TrendingUp, Users as UsersIcon,
    Wallet, Building2, Image as ImageIcon, BedDouble, Tags, Sparkles,
    BarChart3, PlusCircle, CreditCard, CalendarCheck, ListChecks,
} from "lucide-react";

// Role-based menu visibility for hotel panel
const ROLE_MENUS = {
    otel_owner: ["dashboard", "today", "bookings", "manual-booking", "calendar", "yield", "rates", "profile", "amenities", "reviews", "stats", "finance", "pos", "staff", "settings"],
    otel_manager: ["dashboard", "today", "bookings", "manual-booking", "calendar", "yield", "rates", "profile", "amenities", "reviews", "stats", "finance", "pos", "settings"],
    otel_reception: ["dashboard", "today", "bookings", "manual-booking", "calendar"],
    otel_accounting: ["dashboard", "bookings", "finance", "pos", "stats"],
    super_admin: ["dashboard", "today", "bookings", "manual-booking", "calendar", "yield", "rates", "profile", "amenities", "reviews", "stats", "finance", "pos", "staff", "settings"],
    admin: ["dashboard", "today", "bookings", "manual-booking", "calendar", "yield", "rates", "profile", "amenities", "reviews", "stats", "finance", "pos", "staff", "settings"],
};

const NAV = (hotelId) => [
    { key: "dashboard", to: "/h/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "h-nav-dashboard", group: "main" },
    { key: "today", to: "/h/today", icon: CalendarCheck, label: "Bugün", testid: "h-nav-today", group: "main" },
    { key: "bookings", to: "/h/bookings", icon: ClipboardList, label: "Rezervasyonlar", testid: "h-nav-bookings", group: "main" },
    { key: "manual-booking", to: "/h/manual-booking", icon: PlusCircle, label: "Hızlı Rez. Ekle", testid: "h-nav-manual", group: "main" },
    // Inventory & pricing — reuses admin pages (hotel_owner_dep authorizes)
    { key: "calendar", to: hotelId ? `/admin/hotels/${hotelId}/calendar` : "/h/calendar", icon: CalendarDays, label: "Takvim & Fiyat", testid: "h-nav-calendar", group: "inventory" },
    { key: "yield", to: hotelId ? `/admin/hotels/${hotelId}/yield` : "/h/yield", icon: TrendingUp, label: "Yield Manager", testid: "h-nav-yield", group: "inventory" },
    { key: "rates", to: hotelId ? `/admin/hotels/${hotelId}/rates` : "#", icon: Tags, label: "Fiyat Planları", testid: "h-nav-rates", group: "inventory" },
    // Property setup
    { key: "profile", to: hotelId ? `/admin/hotels/${hotelId}/edit` : "#", icon: ImageIcon, label: "Profil, Görseller & Odalar", testid: "h-nav-profile", group: "property" },
    { key: "amenities", to: "/h/amenities", icon: ListChecks, label: "Tesis Özellikleri", testid: "h-nav-amenities", group: "property" },
    { key: "reviews", to: "/h/reviews", icon: MessageSquare, label: "Yorumlar", testid: "h-nav-reviews", group: "property" },
    // Finance & analytics
    { key: "stats", to: "/h/stats", icon: BarChart3, label: "İstatistikler", testid: "h-nav-stats", group: "finance" },
    { key: "finance", to: "/h/finance", icon: Wallet, label: "Finans", testid: "h-nav-finance", group: "finance" },
    { key: "pos", to: "/h/pos", icon: CreditCard, label: "POS Ayarları", testid: "h-nav-pos", group: "finance" },
    // Admin
    { key: "staff", to: "/h/staff", icon: UsersIcon, label: "Personel", testid: "h-nav-staff", group: "admin" },
    { key: "settings", to: "/h/settings", icon: Settings, label: "Otel Ayarları", testid: "h-nav-settings", group: "admin" },
];

const GROUP_LABELS = {
    main: "GENEL",
    inventory: "ENVANTER & FİYAT",
    property: "TESİS YÖNETİMİ",
    finance: "FİNANS & ANALİTİK",
    admin: "AYARLAR",
};

export default function HotelLayout({ children, title, action, hotelName, hotelImage }) {
    const { user, logout } = useAuth() || {};
    const site = useSiteContent();
    const brand = site?.brand || {};
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

    const toggleDark = () => {
        const next = !dark;
        setDark(next);
        document.documentElement.classList.toggle("dark", next);
    };

    const role = user?.role || "otel_reception";
    const allowed = ROLE_MENUS[role] || ROLE_MENUS.otel_reception;
    const hotelId = user?.hotel_id;
    const menu = NAV(hotelId).filter((n) => allowed.includes(n.key));

    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex">
            <aside data-testid="hotel-sidebar" className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
                <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-slate-800">
                    <Link to="/h/dashboard" data-testid="hotel-logo" className="flex items-center gap-2 min-w-0">
                        {brand.logo_url ? (
                            <img src={resolveImageUrl(brand.logo_url)} alt={brand.name || "Logo"} className="h-9 max-h-9 w-auto object-contain" />
                        ) : (
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center font-bold text-white">
                                <Building2 className="h-5 w-5" />
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="text-[9px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold leading-tight">Otel Paneli</div>
                            <div className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate max-w-[150px]" title={hotelName}>{hotelName || "—"}</div>
                        </div>
                    </Link>
                    <button onClick={() => setOpen(false)} className="ml-auto lg:hidden p-2"><X className="h-5 w-5" /></button>
                </div>
                <HotelSwitcher user={user} />
                <nav className="px-3 py-4 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 4rem)" }}>
                    {(() => {
                        const groups = {};
                        menu.forEach((n) => {
                            const g = n.group || "main";
                            if (!groups[g]) groups[g] = [];
                            groups[g].push(n);
                        });
                        return Object.entries(groups).map(([gKey, items]) => (
                            <div key={gKey} className="pb-2">
                                <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">{GROUP_LABELS[gKey] || ""}</div>
                                {items.map((n) => (
                                    <NavLink
                                        key={n.to}
                                        to={n.to}
                                        end={n.end}
                                        data-testid={n.testid}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${isActive
                                            ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300"
                                            : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                                    >
                                        <n.icon className="h-4 w-4" /> {n.label}
                                    </NavLink>
                                ))}
                            </div>
                        ));
                    })()}
                    <div className="pt-3 mt-3 border-t border-slate-100 dark:border-slate-800 space-y-1">
                        {hotelId && (
                            <a href={`/hotel/${hotelId}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="hotel-view-public">
                                <ExternalLink className="h-4 w-4" /> Otel Sayfası
                            </a>
                        )}
                        <button data-testid="hotel-logout" onClick={() => { logout?.(); navigate("/h/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30">
                            <LogOut className="h-4 w-4" /> Çıkış
                        </button>
                    </div>
                </nav>
            </aside>

            {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

            <div className="flex-1 lg:ml-0 min-w-0">
                <header className="sticky top-0 z-20 min-h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center flex-wrap gap-y-2 gap-x-3 px-4 lg:px-8 py-2.5">
                    <button onClick={() => setOpen(true)} className="lg:hidden p-2 -ml-2 flex-shrink-0"><Menu className="h-5 w-5" /></button>
                    {hotelImage && <img src={resolveImageUrl(hotelImage)} alt="" className="h-9 w-9 rounded-lg object-cover hidden md:block" />}
                    <h1 className="font-display text-lg lg:text-xl font-bold text-slate-900 dark:text-white truncate min-w-0 max-w-[40%] flex-shrink" title={title}>{title}</h1>
                    <div className="ml-auto flex items-center flex-wrap gap-2 min-w-0">
                        {action}
                        <button onClick={toggleDark} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0">
                            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </button>
                        <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 whitespace-nowrap" data-testid="hotel-profile-pill">
                            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{user?.full_name?.[0]}</div>
                            <div className="flex flex-col leading-tight">
                                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[140px]">{user?.full_name}</span>
                                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">{user?.role?.replace("otel_", "")}</span>
                            </div>
                        </div>
                    </div>
                </header>
                <main className="p-4 lg:p-8">{children}</main>
            </div>
            <SupportWidget />
        </div>
    );
}
